import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useState } from 'react';
import ProductForm, { ProductFormState } from './ProductForm';
import PageContainer from '../layout/PageContainer';
import { CostingType, validate as validateProduct } from '../../models/Product';
import { FormFieldValue } from '../forms/FormFieldValue';
import { ADD_PRODUCT_MUTATION } from '../../mutations/ProductMutations';
import { useMutation } from '@apollo/client/react';
import { GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import { ProductItemFormState } from '../productItems/ProductItemForm';
import { type ProductItem, validate as validateProductItem } from "../../models/ProductItem";
import { type ProductBulkPricingMarkupLineItem, validate as validateBulkPricingMarkupLineItem } from '../../models/ProductBulkPricingMarkupLineItem';
import { ProductBulkPricingMarkupLineItemFormState } from '../productBulkPricingMarkupLineItems/ProductBulkPricingMarkupLineItemForm';

const INITIAL_FORM_VALUES: Partial<ProductFormState['values']> = {
  items: [],
  name: '',
  costingType: CostingType.STANDARD
};

export default function ProductCreate() {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [createProduct] = useMutation(
    ADD_PRODUCT_MUTATION, {
    refetchQueries: [{ query: GET_PRODUCTS_QUERY }],
  });

  const [formState, setFormState] = useState<ProductFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const [itemErrors, setItemErrors] = useState<Map<number, Partial<Record<keyof ProductItemFormState['values'], string>>>>(
    new Map<number, Partial<Record<keyof ProductItemFormState['values'], string>>>());

  const [bulkPricingMarkupLineItemErrors, setBulkPricingMarkupLineItemErrors] = useState<Map<number, Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>>>(
    new Map<number, Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>>());

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ProductFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ProductFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ProductFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ProductFormState['values']>) => {
        const { issues } = validateProduct(values);

        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const validateItems = useCallback((items: ProductItem[]) => {
    const updatedItemErrors = new Map<number, Partial<Record<keyof ProductItemFormState['values'], string>>>();

    for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
      const item = items[itemIndex];

      const { issues: itemIssues } = validateProductItem(item);

      if (itemIssues && itemIssues.length > 0) {
        const itemErrors = Object.fromEntries(itemIssues.map((issue) => [issue.path?.[0], issue.message]));
        updatedItemErrors.set(itemIndex, itemErrors);
      }
    }

    if (updatedItemErrors.size > 0) {
      setItemErrors(updatedItemErrors);
      return false;
    } else {
      setItemErrors(new Map());
      return true;
    }
  }, []);

  const validateBulkPricingMarkupLineItems = useCallback((bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => {
    const updatedBulkPricingMarkupLineItemErrors = new Map<number, Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>>();

    for (let bulkPricingMarkupLineItemIndex = 0; bulkPricingMarkupLineItemIndex < bulkPricingMarkupLineItems.length; bulkPricingMarkupLineItemIndex++) {
      const bulkPricingMarkupLineItem = bulkPricingMarkupLineItems[bulkPricingMarkupLineItemIndex];

      const { issues: itemIssues } = validateBulkPricingMarkupLineItem(bulkPricingMarkupLineItem);

      if (itemIssues && itemIssues.length > 0) {
        const bulkPricingMarkupLineItemErrors = Object.fromEntries(itemIssues.map((issue) => [issue.path?.[0], issue.message]));
        updatedBulkPricingMarkupLineItemErrors.set(bulkPricingMarkupLineItemIndex, bulkPricingMarkupLineItemErrors);
      }
    }

    if (updatedBulkPricingMarkupLineItemErrors.size > 0) {
      setBulkPricingMarkupLineItemErrors(updatedBulkPricingMarkupLineItemErrors);
      return false;
    } else {
      setBulkPricingMarkupLineItemErrors(new Map());
      return true;
    }
  }, []);

  const handleItemFormFieldChange = useCallback(
    (items: ProductItem[]) => {
      validateItems(items);
    },
    [validateItems],
  );

  const handleBulkPricingMarkupLineItemFieldChange = useCallback(
    (bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => {
      validateBulkPricingMarkupLineItems(bulkPricingMarkupLineItems);
    },
    [validateBulkPricingMarkupLineItems],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async (
    productFormValues: Partial<ProductFormState['values']>,
    items: ProductItem[],
    bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => {
    const { issues } = validateProduct(productFormValues);

    let errors = null;
    let hasErrors = false;

    if (issues && issues.length > 0) {
      errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
      hasErrors = true;
    }

    if ((items || []).length > 0) {
      const itemsValid = validateItems(items);

      if (!itemsValid) {
        hasErrors = true;
      }
    }
    else {
      errors = {
        ...errors,
        items: 'You must add at least one item.'
      };

      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors || {});
      return;
    }

    setFormErrors({});
    setItemErrors(new Map());

    try {
      const { data: result } = await createProduct({
        variables: {
          input: {
            id: 0,
            name: productFormValues.name || 'Unnamed Product',
            sku: productFormValues.sku || null,
            customerId: productFormValues.customerId || 0,
            costingType: productFormValues.costingType || CostingType.STANDARD,
            hasInventory: productFormValues.hasInventory || false,
            items: items,
            bulkPricingMarkupLineItems: bulkPricingMarkupLineItems,
            matchingProductId: productFormValues.matchingProductId
          }
        }
      });

      if (result?.added) {
        notifications.show('Product created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/products');
      } else {
        notifications.show(
          'Failed to create product. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create product. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createProduct, navigate, notifications, setFormErrors, validateItems]);

  return (
    <PageContainer
      title="New Product"
      breadcrumbs={[{ title: 'Products', path: '/dashboard/products' }, { title: 'New' }]}
    >
      <ProductForm
        formState={formState}
        itemErrors={itemErrors}
        bulkPricingMarkupLineItemErrors={bulkPricingMarkupLineItemErrors}
        onFieldChange={handleFormFieldChange}
        onItemFieldChange={handleItemFormFieldChange}
        onBulkPricingMarkupLineItemFieldChange={handleBulkPricingMarkupLineItemFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
        existingProductId={0}
      />
    </PageContainer>
  );
}
